import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { config } from 'dotenv';
import getEnv, { Environment } from '@/utils/environment';
import { SHOPIFY_ENV } from './storefront.types';

const envPath = __dirname.replace('src/clients/storefront', '.env');
const cache = new InMemoryCache();
config({
  path: envPath,
});

const SHOPIFY_STORE_DOMAIN =
  process.env.NEXT_PUBLIC_SHOPIFY_DOMAIN || '0prqvu-0y.myshopify.com';

const PROD_SHOPIFY_STOREFRONT_TOKEN =
  // biome-ignore lint/style/noNonNullAssertion: Automated migration to Biome
  process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_TOKEN!;

const SANDBOX_SHOPIFY_STOREFRONT_TOKEN =
  // biome-ignore lint/style/noNonNullAssertion: Automated migration to Biome
  process.env.NEXT_PUBLIC_SHOPIFY_SANDBOX_STOREFRONT_TOKEN!;

// Documentation and version information https://shopify.dev/docs/api/storefront?version=2025-04
const API_VERSION = '2025-04';
const SHOPIFY_STORE_DEV_DOMAIN =
  'sunrun-subscriptions-test-store.myshopify.com';
export const getStorefrontEndpoint = (storeVersion?: SHOPIFY_ENV) =>
  `https://${storeVersion === SHOPIFY_ENV.DEV ? SHOPIFY_STORE_DEV_DOMAIN : SHOPIFY_STORE_DOMAIN}/api/${API_VERSION}/graphql.json`;

const env = getEnv();

const createStorefrontApolloClient = (storeVersion?: SHOPIFY_ENV) => {
  const storefrontUrl = getStorefrontEndpoint(storeVersion);

  const storefrontToken =
    storeVersion === SHOPIFY_ENV.PROD
      ? PROD_SHOPIFY_STOREFRONT_TOKEN
      : SANDBOX_SHOPIFY_STOREFRONT_TOKEN;

  if (!storefrontToken) {
    throw new Error(
      `No shopify token provided for environment: ${storeVersion}`
    );
  }

  const httpLink = new HttpLink({
    uri: storefrontUrl,
    headers: {
      'X-Shopify-Storefront-Access-Token': storefrontToken,
      'Content-Type': 'application/json',
    },
  });

  const isTestEnv = env !== Environment.PROD;

  const client = new ApolloClient({
    link: httpLink,
    cache: cache,
    devtools: { enabled: isTestEnv },
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
    },
  });

  return client;
};

const getStorefrontApolloClient = (storeVersion: SHOPIFY_ENV): ApolloClient => {
  const storefrontProdClient = createStorefrontApolloClient(SHOPIFY_ENV.PROD);
  const storefrontDevClient = createStorefrontApolloClient(SHOPIFY_ENV.DEV);

  const client =
    storeVersion === SHOPIFY_ENV.PROD
      ? storefrontProdClient
      : storefrontDevClient;
  return client;
};

export default getStorefrontApolloClient;
